import React, { Fragment, useEffect, useState, useRef, useReducer } from "react";
import { eventService } from "client-app-core";
import { Dialog, SelectField, TextField, DatePicker, TimePicker } from "orion-components/CBComponents";
import {
	useMediaQuery,
	MenuItem,
	Button,
	ListItem,
	ListItemText,
	SvgIcon,
	Checkbox,
	FormControlLabel
} from "@mui/material";
import { useTheme, makeStyles } from "@mui/styles";
import moment from "moment";
import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import isNil from "lodash/isNil";
import orderBy from "lodash/orderBy";
import PropTypes from "prop-types";
import EventReOpenAndClose from "./EventReOpenAndClose";
import { eventUpdated } from "orion-components/GlobalData/Actions";
import { EventCard } from "orion-components/Events";
import ErrorBoundary from "orion-components/ErrorBoundary";
import classNames from "classnames";
import { updateEventDialogTemplateSearch } from "orion-components/ContextPanel/Actions";
import { mdiMagnify } from "@mdi/js";
import { eventDialogTemplatesSelector } from "../../../GlobalData/Events/selectors";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";
import { canManageByApplication } from "orion-components/Profiles/Selectors";

const useStyles = makeStyles({
	root: {
		color: "#fff",
		backgroundColor: "#494D53",
		"&:hover": {
			backgroundColor: "#494D53"
		},
		borderRadius: 5,
		paddingLeft: 12,
		paddingRight: 12,
		minHeight: 70
	},
	overrides: {
		margin: 0
	}
});

const initialState = {
	prevEvent: {},
	updatedEvent: {}
};

const eventOptimisticStateReducer = (state, action) => {
	switch (action.type) {
		case "GET_PREV_EVENT":
			return { ...state, prevEvent: action.payload };
		case "GET_UPDATED_EVENT":
			return { ...state, updateEvent: action.payload };
		case "RESET_UPDATED_EVENT":
			console.log("getting dispatched");
			return { ...state, updateEvent: {} };
		default:
			return state;
	}
};

const sortOptions = [
	{ id: "asc", name: "A-Z" },
	{ id: "desc", name: "Z-A" },
	{ id: "date", name: "dateCreated" }
];

const checkboxStyle = {
	"&.Mui-checked": {
		position: "relative",
		"&:after": {
			content: '""',
			left: 13,
			top: 13,
			height: 15,
			width: 15,
			position: "absolute",
			backgroundColor: "#fff",
			zIndex: -1
		}
	}
};

// TODO: Add PropTypes
const EventDialog = props => {
	const { editing, closeDialog, open, selectedEvent, dir, isTemplate, templateSelection } = props;
	const classes = useStyles();

	const [state, stateDispatch] = useReducer(eventOptimisticStateReducer, initialState);

	const dispatch = useDispatch();

	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const eventLookups = useSelector(state => state.eventLookups);
	const allTemplates = useSelector(state => eventDialogTemplatesSelector(state));
	const { types = [], priorities = [] } = eventLookups;
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const canManagePatrolApp = useSelector(state => canManageByApplication(state, "patrols-app", "manage"));
	const orgId = useSelector(state => state.session.user.profile.orgId);

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [startDate, setStartDate] = useState(null);
	const [startTime, setStartTime] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [endTime, setEndTime] = useState(null);
	const [checkTemplate, setCheckTemplate] = useState(false);
	const [template, setTemplate] = useState(-1);
	const [hasManuallyModifiedFields, setManuallyModifiedFields] = useState(false);
	const [sortBy, setSortBy] = useState("asc");
	const [eventType, setEventType] = useState(null);
	const [eventPriority, setEventPriority] = useState(null);
	const [templateSelectionView, setTemplateSelectionView] = useState(false);
	const [nameError, setNameError] = useState(null);
	const [endDateError, setEndDateError] = useState(null);
	const [isPatrolEnabled, setIsPatrolEnabled] = useState(false);

	const theme = useTheme();
	const isXS = useMediaQuery(theme.breakpoints.only("xs"));

	useEffect(() => {
		stateDispatch({ type: "GET_PREV_EVENT", payload: selectedEvent });
		stateDispatch({ type: "GET_UPDATED_EVENT", payload: selectedEvent });
	}, [selectedEvent]);

	const handleChange = (name, setState) => event => {
		// Date Picker event does not have target prop, returns a moment date object
		setManuallyModifiedFields(true);
		const value = event && event.target && !isNil(event.target.value) ? event.target.value : event;
		if (name === "startDate") {
			setStartTime(new Date());
			setStartDate(value);
		}
		if (name === "endDate") {
			setEndTime(new Date());
			setEndDate(value);
			setEndDateError(null);
		}
		if (name === "endTime") {
			if (endDate) {
				const dateVal = updateTime(endDate, value);
				setEndDate(dateVal);
			} else {
				setEndDate(value);
			}
		}
		if (name === "startTime") {
			if (startDate) {
				const dateVal = updateTime(startDate, value);
				setStartDate(dateVal);
			} else {
				setStartDate(value);
			}
		}
		if (name === "name") setNameError(null);

		setState(value);
	};

	const updateTime = (selectedDate, selectedTime) => {
		const updateTime = new Date(selectedTime);
		const dateVal = new Date(selectedDate);
		dateVal.setHours(updateTime.getHours(), updateTime.getMinutes(), updateTime.getSeconds());
		return dateVal;
	};

	const handleSave = (type = null, currentDateTime = null) => {
		const now = new Date();
		const updatedStartDate = startDate ? new Date(startDate.toISOString()) : null;
		const updatedEndDate = endDate !== null ? new Date(endDate.toISOString()) : null;

		if (!name?.replace(/\s/g, "")) {
			setNameError(
				getTranslation(
					`global.profiles.eventProfile.eventDialog.fieldLabel.${checkTemplate ? "templateNameEmpty" : "eventNameEmpty"}`
				)
			);
			return;
		}

		//Setting the start date's seconds and milliseconds to zero for scheduled events
		if (startDate && updatedStartDate > new Date()) {
			updatedStartDate.setSeconds(0);
			updatedStartDate.setMilliseconds(0);
		}

		//Always ensuring the end date has zero seconds and milliseconds when available.
		if (updatedEndDate) {
			updatedEndDate.setSeconds(0);
			updatedEndDate.setMilliseconds(0);
		}

		// Removing leading/trailing whitespaces from event/template name.
		// Any whitespace occurring at least twice in a sequence is replaced with a single space.
		const nameVal = name?.replace(/\s{2,}/g, " ")?.trim();
		const normalizedName = nameVal?.toLowerCase();
		const isDuplicateTemplateName =
			checkTemplate &&
			allTemplates?.some(templateItem => {
				const templateName = templateItem?.name
					?.replace(/\s{2,}/g, " ")
					?.trim()
					?.toLowerCase();

				return (
					templateItem?.ownerOrg === orgId &&
					templateName === normalizedName &&
					templateItem?.id !== selectedEvent?.id
				);
			});

		if (isDuplicateTemplateName) {
			setNameError(getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.templateAlreadyExists"));
			return;
		}
		const event = {
			...selectedEvent,
			name: nameVal,
			desc: description,
			startDate: updatedStartDate?.toISOString() || null,
			endDate: updatedEndDate?.toISOString() || null,
			isTemplate: checkTemplate
		};

		if (type != null) {
			if (type === "close") {
				event.endDate = currentDateTime;
			} else if (type === "re-open") {
				const hasFutureEndDate = updatedEndDate && updatedEndDate > now;
				event.endDate = hasFutureEndDate ? updatedEndDate.toISOString() : null;
			}
		}

		if (template && template !== -1) event["template"] = template;

		if (event.entityData && event.entityData.properties) {
			event.entityData.properties.name = nameVal;
		}

		if (!checkTemplate && isPatrolEnabled && !event?.endDate) {
			if (type === "re-open" && updatedEndDate) {
				setEndDateError(getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.pastEndDate"));
			} else {
				setEndDateError(getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.endDateEmpty"));
			}
			return;
		} else if ((checkTemplate && canManagePatrolApp) || (template && isPatrolEnabled)) {
			event["patrolEnabled"] = isPatrolEnabled;
		}

		event["eventTypeId"] = eventType ? eventType : null;

		event["eventPriorityId"] = eventPriority ? eventPriority : null;

		if (editing) {
			//Implementing optimistic event updates helps mitigate delayed updates on the list panel, which can occur due to a slow network or low-performance system.
			stateDispatch({ type: "GET_UPDATED_EVENT", payload: event });
			dispatch(eventUpdated(selectedEvent.id, event));
			eventService.updateEvent(selectedEvent.id, event, (err, response) => {
				if (err !== null) {
					dispatch(eventUpdated(selectedEvent.id, state.prevEvent));
					stateDispatch({ type: "RESET_UPDATED_EVENT" });
					console.log(err, response);
				}
			});
		} else {
			eventService.createEvent(event, (err, response) => {
				if (err) console.log(err, response);
			});
		}
		handleClose();
	};

	const handleClose = () => {
		dispatch(closeDialog(editing ? "eventEditDialog" : "eventCreateDialog"));
		setName("");
		setDescription("");
		setStartDate(null);
		setEndDate(null);
		setStartTime(null);
		setEndTime(null);
		setCheckTemplate(false);
		setTemplate(-1);
		setManuallyModifiedFields(false);
		setSortBy("asc");
		setEventType(null);
		setEventPriority(null);
		setTemplateSelectionView(false);
		setNameError(null);
		setEndDateError(null);
		dispatch(updateEventDialogTemplateSearch(""));
		setIsPatrolEnabled(false);
	};

	const backClick = () => {
		setTemplateSelectionView(true);
	};

	const newTemplate = isTemplate;
	if (checkTemplate === false && newTemplate === true) {
		setCheckTemplate(true);
	}

	// -- minDate prop on TimePicker doesn't work in the current stable release of material-ui-pickers
	// -- so we are checking the values manually
	const validEndDate = !endDate || endDate > startDate;

	// StartDate is required for Events but not for Templates
	const disabled = Boolean((!name && checkTemplate) || ((!name || !startDate) && !checkTemplate) || !validEndDate);
	const enableTemplateSelection = template === -1 && !selectedEvent && checkTemplate === false && templateSelection;

	const styles = {
		pickers: {
			display: "flex",
			justifyContent: "space-between"
		},
		spacer: {
			padding: 16
		},
		templateIcon: {
			alignItems: "start",
			padding: 0,
			minWidth: 30
		},
		textRightAlign: {
			...(dir === "rtl" && { textAlign: "right" })
		},
		selected: {
			backgroundColor: "#383D48"
		},
		templateGrid: {
			width: isXS ? "auto" : 500,
			display: "grid",
			gap: "10px",
			gridTemplateColumns: "repeat(2, 49%)",
			marginTop: 10
		},
		searchSortGrid: {
			display: "grid",
			gap: "15px",
			gridTemplateColumns: "66% 31%",
			alignItems: "end"
		},
		iconStyle: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: 10,
			borderRadius: 5,
			height: 34,
			width: 34,
			...(dir === "ltr" && { marginRight: "8px" }),
			...(dir === "rtl" && { marginLeft: "8px" })
		},
		listItemText: {
			...(dir === "rtl" && { textAlign: "right" })
		},
		dialogAction: {
			color: "#B2B6BB",
			fontSize: 12,
			fontWeight: 700,
			lineHeight: "unset",
			padding: 0,
			minWidth: "unset",
			...(!enableTemplateSelection && dir === "ltr" && { marginRight: "30px" }),
			...(!enableTemplateSelection && dir === "rtl" && { marginLeft: "30px" })
		},
		ellipsis: {
			width: "100%",
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap"
		}
	};

	const usePrevious = value => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		}, [value]);
		return ref.current;
	};

	const prevProps = usePrevious(props);

	useEffect(() => {
		if (prevProps && !prevProps.open && open && selectedEvent) {
			const {
				name,
				desc,
				startDate,
				endDate,
				isTemplate,
				template,
				eventTypeId,
				eventPriorityId,
				patrolEnabled
			} = selectedEvent;
			setName(name);
			setDescription(desc);
			setStartDate(startDate ? moment(startDate) : null);
			setStartTime(startDate ? startDate : null);
			setEndDate(endDate ? moment(endDate) : null);
			setEndTime(endDate ? endDate : null);
			setCheckTemplate(isTemplate ? isTemplate : false);
			setTemplate(template ? template : -1);
			setIsPatrolEnabled(patrolEnabled ? patrolEnabled : false);
			if (eventTypeId) setEventType(eventTypeId);
			if (eventPriorityId) setEventPriority(eventPriorityId);
		}
	}, [props]);

	useEffect(() => {
		if (!selectedEvent) {
			setStartDate(new Date());
			setStartTime(new Date());
		}
		if (template) {
			const filteredTemplate = allTemplates?.find(item => item.id === template);
			if (filteredTemplate?.name && !editing) setName(filteredTemplate?.name); // only add default template name for new events.
			if (filteredTemplate?.eventTypeId && !editing) setEventType(filteredTemplate?.eventTypeId); // only add default eventType for new events.
		}
	}, [template]);

	const dialogActions = () => {
		return (
			<div style={{ display: "flex", justifyContent: "space-between", padding: "30px 50px" }}>
				<div>
					{!checkTemplate && editing && (
						<EventReOpenAndClose
							startDate={startDate}
							endDate={endDate}
							onSave={handleSave}
							hasManuallyModifiedFields={hasManuallyModifiedFields}
						/>
					)}
				</div>
				<div>
					<Button style={styles.dialogAction} variant="text" onClick={handleClose} key="close-action-button">
						{getTranslation("global.profiles.eventProfile.eventDialog.cancel")}
					</Button>
					{!enableTemplateSelection && !editing && !checkTemplate && !templateSelectionView && (
						<Button
							style={styles.dialogAction}
							variant="text"
							onClick={backClick}
							key="close-action-button"
						>
							{getTranslation("global.profiles.eventProfile.eventDialog.back")}
						</Button>
					)}

					{!enableTemplateSelection && (
						<Button
							color="primary"
							variant="text"
							onClick={() => handleSave()}
							key="save-action-button"
							disabled={disabled}
							style={{
								fontSize: 12,
								fontWeight: 700,
								lineHeight: "unset",
								padding: 0,
								minWidth: "unset"
							}}
						>
							{editing
								? getTranslation("global.profiles.eventProfile.eventDialog.update")
								: getTranslation("global.profiles.eventProfile.eventDialog.create")}
						</Button>
					)}
				</div>
			</div>
		);
	};

	const handleTemplateSearch = e => {
		dispatch(updateEventDialogTemplateSearch(e.target.value));
	};

	const getEventTypeIcon = type => {
		return type?.icon ? (
			<div
				style={{
					...styles.iconStyle,
					backgroundColor: colors[type?.color]?.color || type?.color
				}}
			>
				<SpriteIcon iconName={type.icon} customHeight="21px" customWidth="21px" />
			</div>
		) : null;
	};

	const sortedTemplates =
		sortBy === "date"
			? allTemplates.toSorted((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
			: orderBy(allTemplates, ["name"], [sortBy]);

	return (
		<Dialog
			open={open}
			dir={dir}
			dialogContentStyles={{ padding: "0px 50px" }}
			actions={true}
			DialogActionsFunction={dialogActions}
			title={
				checkTemplate
					? getTranslation("global.profiles.eventProfile.eventDialog.templateTitle")
					: editing
						? getTranslation("global.profiles.eventProfile.eventDialog.editEventTitle")
						: getTranslation("global.profiles.eventProfile.eventDialog.createEventTitle")
			}
			textContent={enableTemplateSelection && getTranslation("global.profiles.eventProfile.eventDialog.desc")}
			titlePropStyles={{ padding: "30px 50px 10px 50px", fontSize: 16, fontWeight: 700 }}
			dialogContentTextStyles={{ fontSize: 14 }}
		>
			{enableTemplateSelection || templateSelectionView ? (
				<Fragment>
					<div style={styles.searchSortGrid}>
						<div
							style={{
								alignItems: "center",
								backgroundColor: "#1f1f21",
								borderRadius: "6px",
								display: "flex",
								height: "30px",
								maxWidth: "380px",
								width: "100%",
								borderColor: "white",
								border: "1px solid #5E657180"
							}}
						>
							<SvgIcon style={{ width: 16, height: 16, marginLeft: 5, color: "#5E657180" }}>
								<path d={mdiMagnify} />
							</SvgIcon>
							<div style={{ width: "100%", marginLeft: 5 }}>
								<input
									type="text"
									placeholder={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.search"
									)}
									style={{
										border: "none",
										color: "#fff",
										backgroundColor: "#1f1f21",
										display: "inline-block",
										fontSize: "13px",
										height: "24px",
										width: "100%",
										outline: "none"
									}}
									onChange={handleTemplateSearch}
								/>
							</div>
						</div>
						<SelectField
							id="sort"
							key="sort"
							label={getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.sortBy")}
							value={sortBy}
							handleChange={e => setSortBy(e.target.value)}
							dir={dir}
							overrides={classes.overrides}
						>
							{sortOptions.map(option => {
								return (
									<MenuItem key={option.id} value={option.id} style={{ display: "flex" }}>
										{getTranslation(
											`global.profiles.eventProfile.eventDialog.fieldLabel.${option.name}`
										)}
									</MenuItem>
								);
							})}
						</SelectField>
					</div>
					<div style={styles.templateGrid}>
						<span>
							<div style={{ width: "100%" }}>
								<ListItem
									button={true}
									style={{ minHeight: 50, padding: "8px 12px", alignItems: "flex-start" }}
									className={classNames(classes.root)}
									onClick={() => {
										handleChange("template", setTemplate)(null);
										setTemplateSelectionView(false);
									}}
								>
									<ListItemText
										primary={getTranslation(
											`global.profiles.eventProfile.eventDialog.fieldLabel.blank`
										)}
										primaryTypographyProps={{
											style: { fontSize: 12, fontWeight: 600 }
										}}
										style={styles.textRightAlign}
									/>
								</ListItem>
							</div>
						</span>
						{sortedTemplates.map(template => {
							return (
								<span key={template.id}>
									<ErrorBoundary>
										<EventCard
											event={template}
											expandable={false}
											eventIconStyles={styles.templateIcon}
											handleSelection={() => {
												handleChange("template", setTemplate)(template.id);
												setTemplateSelectionView(false);
												setIsPatrolEnabled(template?.patrolEnabled ?? false);
											}}
											minimized={true}
											eventTypeId={template?.eventTypeId}
											eventPriorityId={template?.eventPriorityId}
											icon={getEventTypeIcon(
												types?.find(item => item.id === template?.eventTypeId)
											)}
										/>
									</ErrorBoundary>
								</span>
							);
						})}
					</div>
				</Fragment>
			) : (
				<div style={{ width: isXS ? "auto" : 500 }}>
					<TextField
						id="name"
						key="name"
						label={
							checkTemplate
								? getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.templateName")
								: getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.eventName")
						}
						value={name}
						handleChange={handleChange("name", setName)}
						fullWidth={true}
						required={true}
						autoFocus={!editing}
						dir={dir}
						inputLabelStyle={{
							fontSize: 14,
							color: nameError ? "rgb(211, 47, 47)" : "rgb(181, 185, 190)"
						}}
						formControlStyles={{ margin: 0 }}
						error={nameError}
						helperText={nameError}
						disableFocusError={true}
					/>
					<TextField
						id="description"
						key="description"
						label={getTranslation("global.profiles.eventProfile.eventDialog.fieldLabel.description")}
						value={description}
						handleChange={handleChange("description", setDescription)}
						fullWidth={true}
						dir={dir}
						inputLabelStyle={{
							fontSize: 14,
							color: "rgb(181, 185, 190)"
						}}
						formControlStyles={{ margin: "10px 0 0 0" }}
					/>
					{!checkTemplate ? (
						<Fragment>
							<div style={styles.pickers}>
								<DatePicker
									id="start-date"
									key="start-date"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.startDate"
									)}
									value={startDate}
									handleChange={handleChange("startDate", setStartDate)}
									dir={dir}
									options={{ style: { margin: "10px 0 0 0" } }}
								/>
								<div style={styles.spacer} />
								<DatePicker
									id="end-date"
									key="end-date"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.endDate"
									)}
									value={endDate}
									handleChange={handleChange("endDate", setEndDate)}
									minDate={startDate}
									dir={dir}
									options={{ style: { margin: "10px 0 0 0" }, error: endDateError }}
								/>
							</div>
							<div style={styles.pickers}>
								<TimePicker
									id="start-time"
									key="start-time"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.startTime"
									)}
									value={startTime}
									handleChange={handleChange("startTime", setStartTime)}
									format={`time_${timeFormatPreference}`}
									dir={dir}
									options={{ style: { margin: "10px 0 0 0" } }}
								/>
								<div style={styles.spacer} />
								<TimePicker
									id="end-time"
									key="end-time"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.endTime"
									)}
									value={endTime}
									handleChange={handleChange("endTime", setEndTime)}
									minDate={startTime}
									format={`time_${timeFormatPreference}`}
									dir={dir}
									options={{ style: { margin: "10px 0 0 0" } }}
								/>
							</div>
							{!validEndDate && (
								<p
									style={
										dir === "rtl"
											? {
													color: "red",
													fontSize: 12,
													marginRight: 266
												}
											: {
													color: "red",
													fontSize: 12,
													marginLeft: 266
												}
									}
								>
									<Translate value="global.profiles.eventProfile.eventDialog.fieldLabel.endTimeText" />
								</p>
							)}
							<section>
								<SelectField
									id="eventType"
									key="eventType"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.selectEventType"
									)}
									value={eventType}
									handleChange={e => setEventType(e.target.value)}
									dir={dir}
									overrides={classes.overrides}
									formControlProps={{ style: { margin: "10px 0 0 0" } }}
									customIcon={"more"}
									// placeholder={!eventType && "Select One"}
								>
									<MenuItem value={null} style={{ display: "flex" }}>
										<Translate value={"global.profiles.eventProfile.eventDialog.fieldLabel.none"} />
									</MenuItem>
									{types
										.filter(type => type.enabled)
										.map(type => {
											return (
												<MenuItem key={type.id} value={type.id} style={{ display: "flex" }}>
													<ListItem style={{ width: "100%", padding: 0 }}>
														{getEventTypeIcon(type) ?? <></>}
														<div style={{ width: "92%" }}>
															<ListItemText
																primary={type.name}
																style={{
																	color: type.enabled ? "white" : "#6A6C71",
																	...styles.listItemText
																}}
																primaryTypographyProps={{
																	style: styles.ellipsis
																}}
															/>
														</div>
													</ListItem>
												</MenuItem>
											);
										})}
								</SelectField>
							</section>
							<section>
								<SelectField
									id="eventPriority"
									key="eventPriority"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.selectEventPriority"
									)}
									value={eventPriority}
									handleChange={e => setEventPriority(e.target.value)}
									dir={dir}
									overrides={classes.overrides}
									formControlProps={{ style: { margin: "10px 0 0 0" } }}
									// placeholder={!eventPriority && "Select One"}
								>
									{[...priorities]
										.filter(priority => priority.enabled)
										.sort((a, b) => a.order - b.order)
										.map(priority => {
											return (
												<MenuItem
													key={priority.id}
													value={priority.id}
													style={{ display: "flex", ...styles.ellipsis }}
												>
													{priority.name}
												</MenuItem>
											);
										})}
									<MenuItem value={null} style={{ display: "flex" }}>
										<Translate value={"global.profiles.eventProfile.eventDialog.fieldLabel.none"} />
									</MenuItem>
								</SelectField>
							</section>
						</Fragment>
					) : (
						<Fragment>
							<section style={{ marginTop: 10 }}>
								<SelectField
									id="template_eventType"
									key="template_eventType"
									label={getTranslation(
										"global.profiles.eventProfile.eventDialog.fieldLabel.selectEventType"
									)}
									value={eventType}
									handleChange={e => setEventType(e.target.value)}
									dir={dir}
									overrides={classes.overrides}
									// placeholder={!eventType && "Select One"}
								>
									<MenuItem value={null} style={{ display: "flex" }}>
										<Translate value={"global.profiles.eventProfile.eventDialog.fieldLabel.none"} />
									</MenuItem>
									{types.map(type => {
										return (
											<MenuItem key={type.id} value={type.id} style={{ display: "flex" }}>
												<ListItem style={{ width: "100%", padding: 0 }}>
													{getEventTypeIcon(type)}

													<div style={{ width: "76%" }}>
														<ListItemText
															primary={type.name}
															style={{ color: type.enabled ? "white" : "#6A6C71" }}
															primaryTypographyProps={{
																style: styles.ellipsis
															}}
														/>
													</div>
												</ListItem>
											</MenuItem>
										);
									})}
								</SelectField>
								{canManagePatrolApp && (
									<FormControlLabel
										className="themedCheckBox"
										control={
											<Checkbox
												style={{
													transform: "scale(1.1)"
												}}
												checked={isPatrolEnabled}
												onChange={() => setIsPatrolEnabled(!isPatrolEnabled)}
												name="autoExpand"
												sx={checkboxStyle}
											/>
										}
										label={getTranslation(
											"global.profiles.eventProfile.eventDialog.fieldLabel.patrolEnabled"
										)}
										sx={{ color: "#FFFFF", marginTop: "6.5%", marginBottom: "4%" }}
									/>
								)}
							</section>
						</Fragment>
					)}
				</div>
			)}
		</Dialog>
	);
};

EventDialog.propTypes = {
	editing: PropTypes.bool.isRequired,
	closeDialog: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	allTemplates: PropTypes.array.isRequired,
	selectedEvent: PropTypes.object.isRequired,
	dir: PropTypes.string.isRequired,
	isTemplate: PropTypes.bool.isRequired
};

export default EventDialog;
